import { Pattern, match } from "ts-pattern"

export type Result<T = unknown, E = Error> =
    | { readonly _tag: "success"; readonly success: true; readonly data: T }
    | { readonly _tag: "failure"; readonly success: false; readonly error: E }

export const Ok = { _tag: "success", success: true } as const
export const Err = { _tag: "failure", success: false } as const

export class ResultFactory {
    static success<T = unknown, E = Error>(data: T): Result<T, E> {
        return { _tag: "success", success: true, data }
    }

    static failure<T = unknown, E = Error>(error: E): Result<T, E> {
        return { _tag: "failure", success: false, error }
    }
}

export enum TokenErrorType {
    EmptyToken = "Token provided is empty",
    InvalidTokenType = "Invalid token type",
    NullishTokenType = "Token is null or undefined"
}

export class ResultUtil {
    static isResult = <T, E>(value: unknown): value is Result<T, E> => {
        if (typeof value === "object" && value !== null) {
            const asResult = value as Partial<Result<T, E>>
            return (
                (asResult._tag === "success" && "data" in asResult) ||
                (asResult._tag === "failure" && "error" in asResult)
            )
        }
        return false
    }

    static isSuccess<T, E>(
        result: Result<T, E>
    ): result is { readonly _tag: "success"; readonly success: true; readonly data: T } {
        return result._tag === "success"
    }

    static isFailure<T, E>(
        result: Result<T, E>
    ): result is {
        readonly _tag: "failure"
        readonly success: false
        readonly error: E
    } {
        return result._tag === "failure"
    }

    /**
     * Validates and extracts a token from the given input, which can be either a string,
     * a Result<string>, or undefined. It uses pattern matching to handle different scenarios:
     *
     * - For a successful Result<string>, it checks if the contained string is non-empty and returns
     *   a corresponding success or failure Result.
     * - For a failure Result<string>, it returns the failure Result as is.
     * - For a plain string, it validates its non-emptiness and returns a success or failure Result.
     * - For undefined or null, it returns a failure Result indicating an invalid token type.
     * - For inputs that do not match these types, it returns a failure Result indicating an invalid token type.
     *
     * @param {string | Result<string> | undefined} token - The token to be validated and extracted.
     * @returns {Result<string>} - A Result object, either a success containing the valid token or
     *                             a failure with an error message.
     */
    static validateAndExtractToken(token?: string | Result<string>): Result<string> {
        return match<string | Result<string> | undefined, Result<string>>(token)
            .with(Ok, res =>
                res.data.trim() !== ""
                    ? ResultFactory.success(res.data)
                    : ResultFactory.failure(new Error(TokenErrorType.EmptyToken))
            )
            .with(Err, res => ResultFactory.failure(res.error))
            .with(Pattern.string, token =>
                token.trim() !== ""
                    ? ResultFactory.success(token)
                    : ResultFactory.failure(new Error(TokenErrorType.EmptyToken))
            )
            .with(Pattern.nullish, () =>
                ResultFactory.failure(new Error(TokenErrorType.NullishTokenType))
            )
            .otherwise(() =>
                ResultFactory.failure(new Error(TokenErrorType.InvalidTokenType))
            )
    }
}
